import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import UnifiedNavigation from '../../components/Navigation/UnifiedNavigation';
import PropertyHeroSection from '../../components/PropertyHeroSection/PropertyHeroSection';
import PremiumCarousel from '../../components/PremiumCarousel/PremiumCarousel';
import AtacamaVideoSection from '../../components/AtacamaVideoSection/AtacamaVideoSection';
import AtacamaExperiencesSection from '../../components/AtacamaExperiencesSection/AtacamaExperiencesSection';
import AtacamaOffersSection from '../../components/AtacamaOffersSection/AtacamaOffersSection';
import AtacamaMapSection from '../../components/AtacamaMapSection/AtacamaMapSection';
import AtacamaBlogSection from '../../components/AtacamaBlogSection/AtacamaBlogSection';
import InstagramSection from '../../components/BrandHomePage/InstagramSection';
import Footer from '../../components/Footer/Footer';
import { images } from '../../components/shared/assets';
import { useOverlayManager } from '../../contexts/OverlayContext';
import { initializeAllFeatures } from '../../utils/interactiveFeatures';
import '../../styles/property-pages.css';
import './PatagoniaPage.css';

const PatagoniaPageHardcoded: React.FC = () => {
  const { setHoveredDestination } = useOverlayManager();
  const { t } = useTranslation();

  // Initialize all interactive features
  useEffect(() => {
    const cleanup = initializeAllFeatures();
    // Clear any lingering hover state to ensure correct footer variant
    setHoveredDestination(null);
    return cleanup;
  }, []);

  return (
    <div className="property-page property-page--patagonia">
      {/* Navigation - Transparent variant for hero section */}
      <UnifiedNavigation variant="transparent" destination="patagonia" />
      
      {/* Hero Section */}
      <div className="hero-section-wrapper">
        <PropertyHeroSection 
          propertySlug="patagonia"
          customContent={{
            title: t('properties/patagonia:hero.title'),
            copy: '' // Explicitly disable description text
          }}
        />
      </div>

      {/* Content Sections with Scroll Reveals */}
      <div className="content-wrapper">
        
        {/* Premium Carousel - The horizontal carousel we fixed */}
        <div data-scroll-section="carousel" className="scroll-reveal-section">
          <PremiumCarousel />
        </div>


        {/* Full Viewport Video Section */}
        <AtacamaVideoSection />

        {/* Experience Your Own Path Private Excursions Section */}
        <div data-scroll-section="experiences" className="scroll-reveal-section">
          <AtacamaExperiencesSection 
            property="patagonia"
            title="Experience Your Own Path Private Excursions"
            description="Upon arrival at the lodge, your private guide will take your preferences and weather conditions into account to design the ideal itinerary together. Each day you will be able to choose either one Full Day or two Half Day excursions, combining both the National Park's highlights and off-the-beaten-path gems. Meanwhile, enjoy a sneak peek of some of the possibilities."
            restaurantTitle="Fresh fish from the South Pacific fjords. Seasonal produce, local flavours, based on our guests preferences."
            restaurantDescription="Our restaurant, for the exclusive use of Awasi guests, offers a daily menu using the finest local ingredients, adapted to your requirements. We are proud to deliver levels of culinary excellence that define us as members of Relais & Châteaux."
            experiences={[
              {
                image: "/assets/images/patagonia/organized/landscape/patagonia-landscape-view-1.png",
                title: "Kotula Trail",
                category: "PATAGONIA",
                description: "This trek is perfect for those who enjoy staying active and seeking out unique experiences. Situated outside the National Park, the trail is often empty, offering a sense of solitude. While the ascent is relatively steep, it rewards hikers with diverse ecosystems and excellent bird-watching opportunities."
              },
              {
                image: "/assets/images/patagonia/organized/landscape/patagonia-landscape-view-2.png", 
                title: "Base Torres",
                category: "PATAGONIA",
                description: "The Base Torres trail is one of the most iconic and popular in the National Park, but also one of the most challenging. Our journey will take us deep into the Ascencio Valley, culminating at the awe-inspiring granite Torres del Paine and a turquoise glacial lake."
              },
              {
                image: "/assets/images/patagonia/organized/landscape/patagonia-landscape-view-3.png",
                title: "Grey Glacier Lookout", 
                category: "PATAGONIA",
                description: "A close-up glacier viewing experience. This excursion begins with a scenic drive from our lodge to the park's heart, followed by a ferry ride across Lake Pehoe to the trailhead. From Guardería Paine Grande, we'll embark on a hike towards the Southern Patagonian Icefield and the renowned Grey Glacier."
              },
              {
                image: "/assets/images/patagonia/organized/landscape/patagonia-landscape-view-4.png",
                title: "Horseback riding",
                category: "PATAGONIA", 
                description: "Horseback riding is an integral part of gaucho culture and is one of the most authentic Patagonian experiences. Depending on weather and availability your guide will suggest an estancia (ranch) near Torres del Paine National Park for this activity. The total duration will depend on riders' skill and the chosen location."
              }
            ]}
          />
        </div>

        {/* Offers Section */}
        <div data-scroll-section="offers" className="scroll-reveal-section">
          <AtacamaOffersSection />
        </div>

        {/* Map Section */}
        <div data-scroll-section="map" className="scroll-reveal-section">
          <AtacamaMapSection />
        </div>

        {/* Blog Section */}
        <div data-scroll-section="blog" className="scroll-reveal-section">
          <AtacamaBlogSection />
        </div>

        {/* Instagram Section */}
        <InstagramSection />

        {/* Footer */}
        <Footer variant="patagonia" />
      </div>
    </div>
  );
};

export default PatagoniaPageHardcoded;